import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import styles from "./Skeleton.module.scss";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks/useLayoutPropsUtil";

/**
 * Props for the SkeletonText component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "children">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 */
export type SkeletonTextProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> &
  (
    | {
        /**
         * Text variant for body text skeleton.
         * @default body
         */
        variant?: "body";
        /**
         * Number of text lines to display.
         * @default 1
         */
        rows?: number;
      }
    | {
        /**
         * Text variant for headline text skeleton.
         */
        variant?: "headline";
        /**
         * Rows prop is not available for headline variant.
         */
        rows?: never;
      }
  );

/**
 * SkeletonText component for displaying text loading placeholders.
 *
 * Features:
 * - Text skeleton placeholder with body and headline variants
 * - Configurable number of rows for body variant
 * - Animated pulsing effect for loading indication
 * - Accessible with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Customizable styling through className and style props
 * - Progress bar role for screen readers
 * - Different line heights for body vs headline variants
 * - Last line shorter for body variant to simulate natural text
 *
 * @example
 * <Skeleton.Text variant="body" rows={3} />
 *
 * <Skeleton.Text
 *   variant="headline"
 *   className="custom-skeleton"
 *   aria-label="Loading title"
 * />
 */
export const SkeletonText = forwardRef<HTMLDivElement, SkeletonTextProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      "aria-label": ariaLabel = "Loading content",
      variant = "body",
      style,
      ...rest
    } = componentProps;

    const SkeletonClassNames = cx(
      [styles["skeleton"]],
      [styles["text"]],
      [styles[variant]],
      className,
    );

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    const ShaderClassName = cx(styles["line"], styles["shade"]);

    return (
      <div
        {...rest}
        data-anv="skeleton-text"
        role="progressbar"
        aria-busy="true"
        className={SkeletonClassNames}
        style={styleCombined}
        aria-label={ariaLabel}
        ref={ref}
      >
        {"rows" in rest ? (
          Array.from({ length: rest.rows ?? 1 }, (_, i) => i).map((i) => (
            <div key={`line-${i}`} className={ShaderClassName} />
          ))
        ) : (
          <div className={ShaderClassName} />
        )}
      </div>
    );
  },
);

SkeletonText.displayName = "SkeletonText";
